// 公共工具函数

/**
 * 根据agentId查找对应的hostName
 * @param agentId - agent的ID
 * @param agentOptions - agent选项数据
 * @returns hostName
 */
export const findHostNameByAgentId = (agentId: string, agentOptions: any[]): string => {
  if (!agentOptions || !agentId) return ''

  for (const domain of agentOptions) {
    for (const osType of domain.children || []) {
      for (const agent of osType.children || []) {
        if (agent.value === agentId) {
          return agent.hostName || ''
        }
      }
    }
  }
  return ''
}

/**
 * 根据agentId查找对应的完整agent信息
 * @param agentId - agent的ID
 * @param agentOptions - agent选项数据
 * @returns agent信息对象
 */
export const findAgentById = (agentId: string, agentOptions: any[]): any => {
  if (!agentOptions || !agentId) return null

  for (const domain of agentOptions) {
    for (const osType of domain.children || []) {
      for (const agent of osType.children || []) {
        if (agent.value === agentId) {
          return {
            ...agent,
            domainName: domain.label,
            osType: osType.label,
          }
        }
      }
    }
  }
  return null
}

/**
 * 根据emailId查找对应的email和name
 * @param emailId - email的ID
 * @param emailOptions - email选项数据
 * @returns 包含email和name的对象
 */
export const findEmailById = (
  emailId: string,
  emailOptions: any[],
): { email: string; name: string } | null => {
  if (!emailOptions || !emailId) return null

  for (const email of emailOptions) {
    if (email.value === emailId) {
      return {
        email: email.email || '',
        name: email.name || email.label || '',
      }
    }
  }
  return null
}

/**
 * 处理表单提交数据，转换emailIds为emailList
 * @param params - 表单参数
 * @param dataOptions - 数据选项
 * @returns 处理后的参数
 */
export const processEmailIds = (params: any, dataOptions: any): any => {
  if (params.reportConfig?.emailIds?.length > 0) {
    params.reportConfig.emailList = params.reportConfig.emailIds.map((item: string) => ({
      id: item,
      ...findEmailById(item, dataOptions.emailOptions),
    }))
  }
  return params
}

/**
 * 处理设备ID，提取级联选择的最后一级
 * @param params - 表单参数
 * @returns 处理后的参数
 */
export const processDeviceIds = (params: any): any => {
  if (params.deviceIds?.length > 0) {
    params.deviceIds = [params.deviceIds[1]]
  }
  return params
}

/**
 * 处理验证模式中的agentId，提取级联选择的最后一级
 * @param params - 表单参数
 * @returns 处理后的参数
 */
export const processVerificationMode = (params: any): any => {
  // 处理容器Agent到Listener的agentId
  if (params.verificationMode?.containerToListener?.agentId?.length > 0) {
    if (params.os) {
      params.os = [...params.os, params.verificationMode.containerToListener.agentId[1]]
    } else {
      params.os = [params.verificationMode.containerToListener.agentId[1]]
    }

    if (params.verificationModeIds) {
      params.verificationModeIds = [...params.verificationModeIds, 10]
    } else {
      params.verificationModeIds = [10]
    }
    params.verificationMode.containerToListener.agentId =
      params.verificationMode.containerToListener.agentId[2]
    params.verificationMode.containerToListener.listenerHostIp =
      params.verificationMode.containerToListener.listenerHostId[3]
    params.verificationMode.containerToListener.listenerHostId =
      params.verificationMode.containerToListener.listenerHostId[2]
  }

  // 处理Agent到容器的agentId
  if (params.verificationMode?.agentToContainer?.agentId?.length > 0) {
    if (params.os) {
      params.os = [...params.os, params.verificationMode.agentToContainer.agentId[1]]
    } else {
      params.os = [params.verificationMode.agentToContainer.agentId[1]]
    }

    if (params.verificationModeIds) {
      params.verificationModeIds = [...params.verificationModeIds, 5]
    } else {
      params.verificationModeIds = [5]
    }
    params.verificationMode.agentToContainer.agentId =
      params.verificationMode.agentToContainer.agentId[2]
    delete params.verificationMode.agentToContainer.hostId
  }

  params.os = [...new Set(params.os)].join(',')
  params.verificationModeIds = [...new Set(params.verificationModeIds)]

  return params
}

/**
 * 清理不需要的表单字段
 * @param params - 表单参数
 * @returns 清理后的参数
 */
export const cleanupFormParams = (params: any): any => {
  delete params.reportConfig?.emailIds
  delete params.validationMode
  return params
}

/**
 * 处理完整的表单提交数据
 * @param values - 表单值
 * @param dataOptions - 数据选项
 * @returns 处理后的参数
 */
export const processFormData = (values: any, dataOptions: any): any => {
  const params = JSON.parse(JSON.stringify(values))

  // 依次处理各种数据
  processEmailIds(params, dataOptions)
  processDeviceIds(params)
  processVerificationMode(params)
  cleanupFormParams(params)

  return params
}
